import { Box, Stack } from '@mui/material';
import { useRef } from 'react';

import { UserFeedbackModal } from '@/components/modals/UserFeedbackModal/UserFeedbackModal';
import { containsCitationToken } from '@/components/TextEditor/plugins/CitationPlugin/citationTokens';
import { ChatFeedbackButtons } from '@/modules/common/Chat/components/ChatFeedbackButtons';
import { ChatLoadingState } from '@/modules/common/Chat/components/ChatStatusRow';
import { useChatFeedback } from '@/modules/common/Chat/hooks/useChatFeedback';
import { useCopyRenderedContent } from '@/modules/common/Chat/hooks/useCopyRenderedContent';
import { isInteractiveMessage } from '@/modules/household/chat/components/messages/interactive/interactiveMessage.utils';
import { MESSAGE_RENDERERS } from '@/modules/household/chat/components/messages/messageRenderers';
import { MessageType } from '@/types/schema';

import {
  HouseholdChatMessage,
  useHouseholdChatContext,
} from '../contexts/householdChat.context';
import { isTemporaryAssistantMessage } from '../contexts/householdChatTranscript.utils';
import { HouseholdChatActivityTrace } from './HouseholdChatActivityTrace';

interface HouseholdChatAssistantMessageProps {
  isStreaming?: boolean;
  message: HouseholdChatMessage;
}

export function HouseholdChatAssistantMessage({
  isStreaming = false,
  message,
}: HouseholdChatAssistantMessageProps) {
  const activities = message.activities ?? [];
  const hasTextContent = message.content.trim().length > 0;
  // An interactive message renders its own card chrome (title, actions) even
  // when its markdown body is empty, and user feedback applies to generated
  // answers, not to cards soliciting a response.
  const isInteractiveCard = isInteractiveMessage(message);
  const hasRenderableContent =
    hasTextContent ||
    isInteractiveCard ||
    message.type === MessageType.DiagramMessage;

  // While answer tokens are streaming the trace stops showing an in-progress
  // spinner. If the agent keeps running tools past a partial answer, the
  // provider clears the buffered text and the trace spinner resumes.
  const isRunningTools = isStreaming && !hasRenderableContent;

  // Use text when a newer server sends a message type this build does not know.
  const MessageRenderer =
    MESSAGE_RENDERERS[message.type] ?? MESSAGE_RENDERERS[MessageType.Text];

  const { pendingInteractiveMessage } = useHouseholdChatContext();
  // A pending card means the workflow is waiting for the user, not thinking.
  // Keep the streaming placeholder mounted so the turn can resume into it.
  const showThinkingRow =
    isRunningTools && activities.length === 0 && !pendingInteractiveMessage;

  const {
    rating,
    rateMessage,
    isSavingRating,
    isFeedbackModalOpen,
    closeFeedbackModal,
    provideFeedback,
  } = useChatFeedback({
    messageId: message.id,
  });

  const showFeedbackButtons =
    !isStreaming && hasTextContent && !isInteractiveCard;

  const messageContentRef = useRef<HTMLDivElement>(null);
  const handleCopy = useCopyRenderedContent(messageContentRef);
  const handleCopyWithCitation = useCopyRenderedContent(messageContentRef, {
    withCitationTokens: true,
  });
  // The plain rendered-content copy drops citation chips, so messages with
  // citations get a menu offering a variant whose plain-text flavor keeps the
  // inline citation tokens (which paste back as chips in Luminary editors).
  const hasCitations = containsCitationToken(message.content);

  return (
    <>
      {isFeedbackModalOpen && (
        <UserFeedbackModal
          isFeedbackModalOpen={isFeedbackModalOpen}
          closeFeedbackModal={closeFeedbackModal}
          provideFeedback={provideFeedback}
          headingText="Provide feedback"
          placeholderText="How could this response be improved?"
          helpText="This response was generated using Luminary AI. Your feedback helps us improve the household chat experience."
        />
      )}
      <Stack
        data-testid="household-chat-assistant-message"
        sx={{
          alignSelf: 'flex-start',
          maxWidth: '100%',
        }}
      >
        <Stack
          spacing={1}
          sx={{
            py: 1,
          }}
        >
          <HouseholdChatActivityTrace
            activities={activities}
            isStreaming={isRunningTools}
          />
          {showThinkingRow && <ChatLoadingState />}
          {hasRenderableContent && (
            <Box ref={messageContentRef}>
              <MessageRenderer message={message} />
            </Box>
          )}
        </Stack>
        {showFeedbackButtons && (
          <ChatFeedbackButtons
            rating={rating}
            onRate={(newRating) => {
              void rateMessage(newRating);
            }}
            ratingDisabled={
              isSavingRating || isTemporaryAssistantMessage(message.id)
            }
            onCopy={handleCopy}
            onCopyWithCitation={
              hasCitations ? handleCopyWithCitation : undefined
            }
          />
        )}
      </Stack>
    </>
  );
}
